import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { FiMail, FiArrowLeft } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { getLastLoginInfo } from "../../utils/lastLoginInfo";

const getFriendlyMagicLinkError = (message = "") => {
  const normalized = message.toLowerCase();
  if (normalized.includes("temporarily paused") || normalized.includes("temporarily unavailable")) {
    return "Service is temporarily unavailable. Please try again shortly.";
  }
  if (normalized.includes("not allowed") || normalized.includes("email_blocked")) {
    return "This email address is not allowed. Please use a different email.";
  }
  if (normalized.includes("too many verification requests") || normalized.includes("too many verification attempts")) {
    return "We've received a lot of requests. Please wait a couple of minutes and try again.";
  }
  return message || "An error occurred. Please try again.";
};

const CheckoutAuthStep = ({ plan, interval, extensionAuthOptions, subtitle }) => {
  const { sendMagicLink, completeLoginCode, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [error, setError] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState("");
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [lastLoginInfo, setLastLoginInfo] = useState(null);
  const codeInputRefs = useRef([]);
  const codeClientRef = useRef(null);
  const googleClientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
  const googleRedirectUri =
    process.env.GOOGLE_OAUTH_REDIRECT_URI ||
    (typeof window !== "undefined" ? `${window.location.origin}/auth/google/callback` : undefined);

  // Tick down the resend cooldown once per second
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Load last login info
  useEffect(() => {
    const info = getLastLoginInfo();
    if (info) {
      setLastLoginInfo(info);
      if (info.method === "email" && info.email) {
        setEmail(info.email);
      }
    }
  }, []);

  // Initialize Google OAuth client
  useEffect(() => {
    if (!googleClientId || typeof window === "undefined") return;

    let cancelled = false;
    const scriptId = "google-identity-services";
    let script = document.getElementById(scriptId);

    const initializeClient = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.oauth2) return;

      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: googleClientId,
        scope: "openid email profile",
        ux_mode: "popup",
        redirect_uri: googleRedirectUri,
        callback: async (response) => {
          if (response.error) {
            setError("Google sign-in was cancelled. Please try again.");
            setGoogleLoading(false);
            return;
          }
          if (!response.code) {
            setError("Google did not return an authorization code.");
            setGoogleLoading(false);
            return;
          }
          try {
            await loginWithGoogle(response.code, extensionAuthOptions || {});
          } catch (err) {
            setError(err.message || "Google sign-in failed. Please try again.");
          } finally {
            setGoogleLoading(false);
          }
        },
      });
      setGoogleReady(true);
    };

    if (window.google?.accounts?.oauth2) {
      initializeClient();
      return;
    }

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        initializeClient();
      };
      script.onerror = () => {
        setError("Unable to load Google sign-in. Please check your connection.");
      };
      document.body.appendChild(script);
    } else if (script.dataset.loaded === "true") {
      initializeClient();
    } else {
      script.addEventListener("load", initializeClient, { once: true });
    }

    return () => {
      cancelled = true;
      script?.removeEventListener?.("load", initializeClient);
    };
  }, [googleClientId, googleRedirectUri, loginWithGoogle]);

  const handleGoogleSignIn = () => {
    setError("");
    if (!googleClientId) {
      setError("Google sign-in is not configured.");
      return;
    }
    if (!googleReady || !codeClientRef.current) {
      setError("Google sign-in is still loading. Please try again in a moment.");
      return;
    }
    setGoogleLoading(true);
    codeClientRef.current.requestCode();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError("");

    try {
      const emailToSend = email;
      await sendMagicLink(email, extensionAuthOptions || {});
      setSentEmail(emailToSend);
      setCodeDigits(["", "", "", "", "", ""]);
      setCodeError("");
      setEmail("");
      setResendCooldown(30);
      // Focus first code input after slide animation completes
      setTimeout(() => codeInputRefs.current[0]?.focus(), 450);
    } catch (err) {
      setError(getFriendlyMagicLinkError(err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = codeDigits.join("");
    if (code.length !== 6) {
      setCodeError("Please enter all 6 digits");
      return;
    }
    setCodeVerifying(true);
    setCodeError("");
    try {
      await completeLoginCode(sentEmail, code, extensionAuthOptions || {});
    } catch (err) {
      setCodeError(getFriendlyMagicLinkError(err.message));
      setCodeDigits(["", "", "", "", "", ""]);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } finally {
      setCodeVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLoading || resendCooldown > 0) return;
    setResendLoading(true);
    setCodeError("");
    setCodeDigits(["", "", "", "", "", ""]);
    try {
      await sendMagicLink(sentEmail);
      setResendCooldown(30);
    } catch (err) {
      setCodeError(getFriendlyMagicLinkError(err.message));
    } finally {
      setResendLoading(false);
    }
    setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
  };

  const showCodeView = Boolean(sentEmail);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
        <div className="relative">
          {/* Sliding container - both panels side by side */}
          <div
            className="flex transition-transform duration-400 ease-in-out"
            style={{ transform: showCodeView ? "translateX(-100%)" : "translateX(0)" }}
          >
            {/* Panel 1: Email + Google sign-in */}
            <div className="w-full flex-shrink-0 p-8">
              {/* Brand + Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/15 flex-shrink-0">
                    <div className="w-full h-full bg-white dark:bg-zinc-900 rounded-[10px] p-0.5 flex items-center justify-center">
                      <img src="/logo-128.png" alt="Ultimate Web Scraper" className="w-full h-full" />
                    </div>
                  </div>
                  <span className="text-sm font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent tracking-tight">
                    Ultimate Web Scraper
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-light leading-[1.1] tracking-tight text-gray-700 dark:text-zinc-200 mb-1.5">
                  <span>Sign in</span>
                  <span className="mx-2 sm:mx-2.5 select-none font-thin text-gray-400 dark:text-zinc-500">/</span>
                  <span>Create account</span>
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-zinc-400 leading-relaxed max-w-md">
                  {subtitle || "Create a free account or sign in to complete your purchase"}
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="mb-4 bg-red-500/5 dark:bg-red-500/[0.03] border border-red-500/20 text-red-600 dark:text-red-300 px-4 py-3 rounded-xl text-xs sm:text-sm animate-in fade-in slide-in-from-top-2 duration-300"
                >
                  <p className="leading-relaxed">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div className="relative">
                  <label htmlFor="checkout-email" className="sr-only">Email address</label>
                  <input
                    id="checkout-email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitLoading || googleLoading}
                    className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-white/60 dark:bg-zinc-800/60 backdrop-blur-xl text-gray-900 dark:text-zinc-100 text-sm sm:text-base border border-gray-300/40 dark:border-zinc-700/40 rounded-xl focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus:bg-white/80 dark:focus:bg-zinc-800/80 focus-visible:outline-none placeholder-gray-400 dark:placeholder-zinc-500 transition-all duration-300 hover:border-gray-400/50 dark:hover:border-zinc-600/50 disabled:opacity-50 disabled:cursor-not-allowed font-mono tracking-wide"
                    placeholder="you@company.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitLoading || googleLoading}
                  className="relative group w-full py-2.5 sm:py-3 px-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-xs sm:text-sm rounded-xl hover:shadow-2xl hover:shadow-blue-400/30 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 shadow-xl border border-blue-300/50 active:scale-[0.98] overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center gap-2 uppercase tracking-wide">
                    {submitLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FiMail className="w-4 h-4" />
                        <span>Email me login code</span>
                      </>
                    )}
                  </span>
                </button>
              </form>

              {/* Divider */}
              <div className="mt-5">
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-gray-300/40 dark:border-zinc-700/40" />
                  <span className="text-xs text-gray-500 dark:text-zinc-500 font-medium tracking-wide">or</span>
                  <div className="flex-1 border-t border-gray-300/40 dark:border-zinc-700/40" />
                </div>

                {/* Google button */}
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading || submitLoading || !googleReady || !googleClientId}
                    className="relative group w-full flex items-center justify-center gap-2.5 px-4 py-2.5 sm:py-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 hover:scale-[1.02] hover:shadow-xl hover:shadow-gray-500/10 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100/50 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                    {googleLoading ? (
                      <svg className="relative animate-spin h-5 w-5 text-gray-900" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <FcGoogle className="relative w-5 h-5 flex-shrink-0" />
                    )}
                    <span className="relative text-xs sm:text-sm font-semibold text-gray-900">
                      {googleLoading ? "Connecting..." : "Continue with Google"}
                    </span>
                  </button>
                </div>
              </div>

              {/* Last login hint */}
              {lastLoginInfo && (
                <div className="mt-5 pt-4 border-t border-gray-200/60 dark:border-zinc-700/40">
                  <p className="text-[11px] text-gray-500 dark:text-zinc-400 text-center flex items-center justify-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/60" />
                    <span>
                      Last signed in with {lastLoginInfo.method === "google" ? "Google" : "Email"}
                      {lastLoginInfo.email && (
                        <span className="text-gray-400 dark:text-zinc-500"> &middot; {lastLoginInfo.email}</span>
                      )}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Panel 2: Code verification */}
            <div className="w-full flex-shrink-0 p-8">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-800 dark:text-zinc-100 mb-2">
                  Enter login code
                </h2>
                <p className="text-sm text-gray-500 dark:text-zinc-400 leading-relaxed">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-gray-700 dark:text-zinc-300">{sentEmail}</span>
                </p>
              </div>

              {codeError && (
                <div className="mb-4 bg-red-500/5 dark:bg-red-500/[0.03] border border-red-500/20 text-red-600 dark:text-red-300 px-4 py-2.5 rounded-xl text-xs sm:text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                  {codeError}
                </div>
              )}

              {/* 6-digit code inputs */}
              <div className="flex gap-2 sm:gap-3 justify-center mb-6">
                {codeDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (codeInputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    disabled={codeVerifying}
                    className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold bg-gray-50 dark:bg-zinc-800/80 border border-gray-300/60 dark:border-zinc-600/40 rounded-lg text-gray-900 dark:text-zinc-100 focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 focus-visible:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (!val && !digit) return;
                      const newDigits = [...codeDigits];
                      newDigits[idx] = val.slice(-1);
                      setCodeDigits(newDigits);
                      setCodeError("");
                      if (val && idx < 5) {
                        codeInputRefs.current[idx + 1]?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !digit && idx > 0) {
                        const newDigits = [...codeDigits];
                        newDigits[idx - 1] = "";
                        setCodeDigits(newDigits);
                        codeInputRefs.current[idx - 1]?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                      if (!pasted) return;
                      const newDigits = [...codeDigits];
                      for (let i = 0; i < 6; i++) {
                        newDigits[i] = pasted[i] || "";
                      }
                      setCodeDigits(newDigits);
                      setCodeError("");
                      const focusIdx = Math.min(pasted.length, 5);
                      codeInputRefs.current[focusIdx]?.focus();
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                onClick={handleVerifyCode}
                disabled={codeVerifying || codeDigits.join("").length !== 6}
                className="w-full py-2.5 sm:py-3 px-4 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 text-black font-bold text-xs sm:text-sm rounded-xl hover:shadow-xl hover:shadow-blue-400/20 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 active:scale-[0.98] border border-blue-300/50 mb-4"
              >
                {codeVerifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  "Verify code"
                )}
              </button>

              {/* Spam folder notice */}
              <div className="mb-4 flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-red-500/[0.06] dark:bg-red-500/[0.05] border border-red-400/20 dark:border-red-400/15">
                <FiMail className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 dark:text-red-300/90 leading-relaxed">
                  Don't see the code? <span className="font-semibold">Check your spam folder</span> — sometimes it ends up there.
                </p>
              </div>

              {/* Back + Resend */}
              <div className="flex items-center justify-center gap-3 text-xs">
                <button
                  onClick={() => {
                    setSentEmail("");
                    setCodeDigits(["", "", "", "", "", ""]);
                    setCodeError("");
                  }}
                  className="text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 transition-colors flex items-center gap-1"
                >
                  <FiArrowLeft className="w-3 h-3" />
                  Back
                </button>
                <span className="text-gray-300 dark:text-zinc-700">|</span>
                <button
                  onClick={handleResendCode}
                  disabled={resendLoading || resendCooldown > 0}
                  className="text-blue-500 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-300 transition-colors disabled:text-gray-400 dark:disabled:text-zinc-600 disabled:cursor-not-allowed disabled:hover:text-gray-400 dark:disabled:hover:text-zinc-600"
                >
                  {resendLoading
                    ? "Sending..."
                    : resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend code"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutAuthStep;
